import React from "react";
import { Control, actions } from "react-redux-form";
import { Row, Col } from "reactstrap";
import { compose, withHandlers } from "recompose";
import shortid from "shortid";
import { get } from "lodash";
import { NotificationManager } from "react-notifications";
import { Trans } from "react-i18next";
import deepPick from "deepPick";
import {
  FormGroup,
  DatePicker,
  DueDatePicker,
  required,
  Page,
  onMount,
  withReload,
  withLocalForm,
} from "components/common";
import {
  withXeroErrors,
  LineItems,
  SalesEditActions,
  withContactSearch,
  Attachments,
  withDocumentRenderer,
  withOptions,
  Payment,
} from "components";
import { InvoiceDocument, SmallInvoiceDocument } from "components/documents";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import { format_reference, formatDate } from "../../../services/util";
import moment from "moment";
import BackButton from "components/refactoring/BackButton";
import { filterAndValidateLineItems, getBlankLineItems } from "../../../components/LineItems";

const PrintCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `Invoice_${item.InvoiceNumber}.pdf`
    ),
})(({ PrintDocument, DocumentRenderer, i }) => (
  <td className="text-end text-left">
    <PrintDocument item={i} />
    <DocumentRenderer component={SmallInvoiceDocument} />
  </td>
));

const ShareCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `Invoice_${item.InvoiceNumber}.pdf`,
    ),
})(({ DocumentRenderer, ShareDocument, i, componentDoc }) => (
  <td className="text-end text-left">
    <ShareDocument item={i} wtsp={true} />
    <DocumentRenderer component={componentDoc} />
  </td>
));

export const EditInvoice = ({
  item,
  LocalForm,
  onSubmit,
  xero,
  handleSave,
  dispatch,
  handlePaymentAdded,
  handleAttachmentsChanged,
  ContactSearch,
  XeroErrors,
  InvoiceOptions,
  organisation,
  DocumentRenderer,
  DownloadDocument,
  EmailDocument,
  history,
  t,
}) => {

  const viewLink = () => {
    const { InvoiceID } = item;

    history.push(`/accounts_receivable/invoices/view/${InvoiceID}`)
  };

  return (
    <div className="nw-invoiceStyle">
      <Page
        title={
          item.InvoiceID
            ? t(`Edit Invoice {{ InvoiceNumber }}`, {
              InvoiceNumber: item.InvoiceNumber,
            })
            : t("Create New Invoice")
        }
        translate={false}
      >
        <BackButton />
        <XeroErrors />
        <DocumentRenderer component={InvoiceDocument} />
        <div className="d-flex align-items-center" style={{ paddingBottom: 20 }}>
          {item.SentToContact && (
            <div className="text-success small">
              <span className="fa fa-check mr-1" /> <Trans>Sent to contact</Trans>
            </div>
          )}
          <div className="mx-auto" />
          {!!(item.InvoiceID && item.Attachments) && (
            <Attachments
              xero={xero}
              onAttachmentsChanged={handleAttachmentsChanged}
              baseUrl={`invoices/${item.InvoiceID}`}
              attachments={item.Attachments}
            />
          )}
          {!!item.InvoiceID && <DownloadDocument className="mr-2" />}
          {!!item.InvoiceID && <EmailDocument type="SALES_INVOICE" item={item} />}
          <div className="mx-1" />
          {!!item.InvoiceID && <PrintCell i={item} />}
          <div className="mx-1" />
          {!!item.InvoiceID && <ShareCell i={item} componentDoc={InvoiceDocument} />}
          <div className="mx-1" />
          {!!item.InvoiceID && <InvoiceOptions />}
          {!!item.InvoiceID && <div className="">
            <button
              type="button"
              className="btn-sm btn btn-primary"
              onClick={viewLink}>
              <Trans>View Invoice</Trans>
            </button>
          </div>}
        </div>
        <LocalForm className="mt-3"
          onSubmit={onSubmit}
          validators={{
            Contact: { required },
            DocumentType: { required },
            Date: { required },
          }}
        >
          <Row>
            <Col xs={false} md={3}>
              <FormGroup label="To" model=".Contact" addControlClass={false}>
                <ContactSearch model=".Contact" style={{ backgroundColor: '#F4F7FE' }} />
              </FormGroup>
            </Col>
            <Col xs={false} md={3}>
              <FormGroup label="Status">
                <Control.text model=".Status" readOnly />
              </FormGroup>
            </Col>
            <Col xs={false} md={3}>
              <FormGroup label="Date" model=".Date">
                <DatePicker type="date" model=".Date" className="form-control" />
              </FormGroup>
            </Col>
            <Col xs={false} md={3}>
              <FormGroup label="Due Date" model=".DueDate">
                <DueDatePicker model=".DueDate" className="form-control" defaultValue={moment(new Date())} />
              </FormGroup>
            </Col>
            <Col xs={false} md={3} className="pt-2 pb-2">
              <FormGroup
                label="Supply Date"
                model=".ExpectedPaymentDate"
              >
                <DatePicker type="date"
                  model=".ExpectedPaymentDate"
                  className="form-control"
                  defaultValue={moment(new Date())}
                />
              </FormGroup>
            </Col>
            {/* <Col xs={false} md={3} className="pt-2 pb-2">
          {item.HesaabNumber? 
            <FormGroup label="Hessab Invoice Number #" model=".InvoiceNumber">
              <Control.text
                model=".HesaabNumber"
                placeholder=""
                readOnly
                /> 
              </FormGroup>
            :
            <FormGroup label="Hessab Invoice Number #" model=".InvoiceNumber">
              <Control.text
                model=".InvoiceNumber"
                placeholder=""
                readOnly
              />
            </FormGroup>
          }
          </Col> */}
            <Col xs={false} md={3} className="pt-2 pb-2">
              <FormGroup label="Invoice Number" model=".InvoiceNumber">
                <Control.text
                  model=".InvoiceNumber"
                  placeholder=""
                  readOnly
                  disabled
                />
              </FormGroup>
            </Col>
            <Col xs={false} md={3} className="pt-2 pb-2">
              <FormGroup label="Reference" model=".Reference">
                <Control.text updateOn="blur" model=".Reference" />
              </FormGroup>
            </Col>
            <Col xs={false} md={3} className="pt-2 pb-2">
              <FormGroup label="Document Type" model=".DocumentType">
                <Control.select model=".DocumentType" defaultValue={item.DocumentType}>
                <option disabled selected value> ------- </option>
                  {[{'label': 'Tax Invoice', 'value': 'standard'}, {'label': 'Simplified Invoice', 'value': 'simplified'}].map((i) => (
                    <option key={i.value} value={i.value}>
                      {i.label}
                    </option>
                  ))}
                </Control.select>
              </FormGroup>
            </Col>
          </Row>
          <hr />
          <LineItems
            isNewInvoice={item.InvoiceID ? false : true}
            organisation={organisation}
            item={item}
            includeDiscount={item.Type === 'ACCREC'}
            dispatch={dispatch}
            xero={xero}
          />
          <SalesEditActions onSave={handleSave} item={item} />
        </LocalForm>
        <div className="pt-3">
        </div>
      </Page>
      {item.Status === "AUTHORISED" && (
        <>
          <hr />
          <Payment
            invoice={item}
            organisation={organisation}
            onPaymentAdded={handlePaymentAdded}
            xero={xero}
          />
        </>
      )}
    </div>
  );
};

const enhance = compose(
  withOrganisationContextConsumer,
  withReload(),
  onMount(({ match, xero, location, fetchOrganisation }) => () => {
    const { id } = match.params;
    let item = null;

    if (id === "new") {
      item = get(location, "state.copy") || {
        LineItems: getBlankLineItems(),
        Status: "DRAFT",
        Type: "ACCREC",
        Date: formatDate(moment()),
      };
      item = {...item, Reference: format_reference(item.Reference)}
    } else {
      item = xero
        .get(`invoices/${match.params.id}`)
        .get("data")
        .then((item) => ({
          ...item,
          LineItems: item.LineItems.map((i) => ({
            ...i,
            _id: shortid(),
          })),
        }));
    }

    return Promise.props({
      item,
      organisation: fetchOrganisation(),
    });
  }),
  withLocalForm({
    initialState: ({ item }) => item,
    stateKey: "item",
    formKey: "form",
  }),
  withContactSearch(),
  withXeroErrors(),
  withHandlers({
    handleAttachmentsChanged:
      ({ xero, match, dispatch }) =>
        () => {
          xero
            .get(`invoices/${match.params.id}/attachments`)
            .get("data")
            .tap((d) => dispatch(actions.change("local.Attachments", d)));
        },
    handleSave:
      ({ item, xero, history, match, setXeroErrors, reload, t, form, dispatch }) =>
        ({ draft, approve, submit, addAnother } = {}) => {
          const incompleteForm = "Incomplete Form"
          const contactValid = form.Contact?.$form?.valid !== undefined
            ? form?.Contact?.$form?.valid
            : form?.Contact?.valid;
          const dateValid = form?.Date?.valid || false
          const dueDateValid = item?.DueDate
          const DocumentType = item?.DocumentType

          if (!contactValid) {
            NotificationManager.error(t("Please Enter A Contact Name"), incompleteForm);
          } else if (!dateValid) {
            NotificationManager.error(t("The Date is empty, please add a date."), incompleteForm);
          } else if (dueDateValid === null) {
            NotificationManager.error(t("Due Date is empty, please add a duedate."), incompleteForm);
          } else if (!DocumentType) {
            NotificationManager.error(t("Please Enter A DocumentType"), incompleteForm);
            return
          }

          const { result: LineItems, error } = filterAndValidateLineItems(
            dispatch
          )(item.LineItems);

          if (error) {
            return;
          }

          let Status = item.Status;
          if (draft) Status = "DRAFT";
          if (approve) Status = "AUTHORISED";
          if (submit) Status = "SUBMITTED";
          setXeroErrors(null);
          const { InvoiceID } = item;

          const allowedProps = [
            "Type",
            "Contact.ContactID",
            "Date",
            "DueDate",
            "Status",
            "LineAmountTypes",
            "LineItems",
            "SubTotal",
            "TotalTax",
            "Total",
            "TotalDiscount",
            "CurrencyCode",
            "CurrencyRate",
            "InvoiceID",
            "InvoiceNumber",
            "Reference",
            "ExpectedPaymentDate",
            "PlannedPaymentDate",
            "DocumentType",
          ];

          const lines = item.LineItems.map((el, index) => {
            const lineItem = item?.LineItems[index];

            if (lineItem?.ItemCode || lineItem?.Description) {
              if (!lineItem?.UnitAmount && !lineItem?.ItemCode) {
                return { ...el, DiscountRate: 0, UnitAmount: 0, DiscountAmount: 0 };
              } else {
                return el;
              }
            }
            return el;
          });

          const linesWithoutLineAmount = lines.map(line => {
            const { LineAmount, ...rest } = line;
            return rest;
          });

          const linesRefactor = linesWithoutLineAmount.filter((el, index) => {
            if (lines[index]?.ItemCode || lines[index]?.Description) {
              return el;
            }
          });

          item = { ...item, LineItems: linesRefactor }

          {/* Filter End Line Items */ }

          const allowedItem = deepPick(item, allowedProps);

          const p = InvoiceID
            ? xero.put(`invoices/${InvoiceID}`, {
              ...allowedItem,
              Status,
              // LineItems,
            })
            : xero.post("invoices", {
              ...allowedItem,
              Status,
              // LineItems,
            });

          p.then(({ data: { InvoiceID } }) => {
            NotificationManager.success(t("Invoice saved successfully"));

            if (addAnother) {
              history.push(match.path.replace(":id", "new"));
            } else {
              history.replace(`/accounts_receivable/invoices/view/${InvoiceID}?auto_report=true`);
            }
            reload();
          }, setXeroErrors);
        },
    handlePaymentAdded:
      ({ reload }) =>
        () => {
          reload();
        },
  }),
  withOptions(),
  withDocumentRenderer({
    onDownload:
      ({ item, xero }) =>
        (getDocument) =>
          getDocument(
            {
              item,
            },
            `invoice_${item.InvoiceNumber}.pdf`
          ),
    type: "SALES_INVOICE",
  })
);

export default enhance(EditInvoice);
